import { Transition } from '@headlessui/react';
import { useEffect, useRef } from 'react';
import { CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME } from '../constants';
import PopoverCard from '@/components/molecules/PopoverCard/PopoverCard';

/**
 * The popover the editor's left nav opens to browse an asset type — highlight reels today, clips
 * once ClipListPopover is retired.
 *
 * It owns everything that must be identical across asset types: where the popover sits, its size,
 * the PopoverCard it's built from, the open/close motion, and dismissal. Callers supply the heading,
 * the body and a close handler — nothing positional, and there is deliberately no escape hatch for
 * geometry or chrome, because an asset type that could opt out of them would drift from the others.
 *
 * Dismissal is a pass-through `mousedown` listener rather than the full-screen click-catcher the
 * clip customizer popups use. The catcher swallows the click that dismisses it, which would stop a
 * nav item from navigating while a popover is open — here the click both closes and lands.
 */

// Top clears ClipHeader's h-16 plus its 1px bottom border; bottom matches ClipNewTimeline's mb-2,
// so the popover ends on the same line the timeline does. Left places the popover just past the w-20
// nav rail, per the design.
const POSITION_CLASS = 'absolute bottom-2 left-[86px] top-[71px] z-[100]';

const CARD_CLASS_NAMES = {
  container: 'h-full w-[31.5rem]',
  // 16px top + 28px heading + 12px = the design's 56px header.
  headingRow: 'mb-3'
};

// Enter only: the popover unmounts when closed, so there is no subtree left to animate out.
const TRANSITION_CLASSES = {
  enter: 'transition duration-150 ease-out',
  enterFrom: 'transform translate-x-2 scale-[0.98] opacity-0',
  enterTo: 'transform translate-x-0 scale-1 opacity-100'
};

export interface AssetPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  heading: string;
  headingBadge?: React.ReactNode;
  /** Renders a back arrow left of the heading. Omit it and there's no back affordance. */
  onBack?: () => void;
  footer?: React.ReactNode;
  bodyClassName?: string;
  closeTrackingId?: string;
  backTrackingId?: string;
}

export default function AssetPopover({
  isOpen,
  onClose,
  children,
  heading,
  headingBadge,
  onBack,
  footer,
  bodyClassName,
  closeTrackingId,
  backTrackingId
}: AssetPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    /**
     * Dialogs opened from inside a popover are portalled to document.body, so they sit outside
     * popoverRef and an interaction with one reads as an outside click. They opt out by carrying
     * CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME on their root, which covers their backdrop as well as
     * their panel. Any dialog a consumer opens from inside the popover has to carry it.
     */
    const isInsidePopover = (target: HTMLElement) =>
      !!popoverRef.current?.contains(target) || !!target.closest(`.${CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}`);

    const handleMouseDown = (event: MouseEvent) => {
      if (!isInsidePopover(event.target as HTMLElement)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // A dialog opened from inside the popover handles Escape itself; without this guard one
      // keypress dismisses the dialog and tears down the popover behind it.
      if (event.key === 'Escape' && !document.querySelector(`.${CLIP_POPOVER_ASSUME_INSIDE_CLASS_NAME}`)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <div ref={popoverRef} data-testid="asset-popover" className={POSITION_CLASS}>
      <Transition show appear className="h-full" {...TRANSITION_CLASSES}>
        <PopoverCard
          heading={heading}
          headingAccessory={headingBadge}
          onClose={onClose}
          onBack={onBack}
          closeTrackingId={closeTrackingId}
          backTrackingId={backTrackingId}
          footer={footer}
          classNames={{ ...CARD_CLASS_NAMES, body: bodyClassName }}
        >
          {children}
        </PopoverCard>
      </Transition>
    </div>
  );
}
